import React, { forwardRef, useState } from 'react';
import { Avatar as MuiAvatar } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import { useDsTokens } from '../theme/useDsTokens';
import { cn } from '../utils/cn';
import { Spinner } from './Spinner';
import { AvatarSize, DsPrimitiveProps } from './types';

export interface AvatarProps extends DsPrimitiveProps {
  size?: AvatarSize;
  src?: string;
  alt?: string;
  /** Initials fallback (1–2 chars) */
  children?: string;
  loading?: boolean;
  bordered?: boolean;
}

const SIZE_MAP: Record<AvatarSize, number> = {
  sm: 24,
  md: 32,
  lg: 40,
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  {
    id,
    className,
    size = 'md',
    src,
    alt,
    children,
    loading = false,
    bordered = false,
    'aria-label': ariaLabel,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();
  const [imgError, setImgError] = useState(false);
  const dimension = SIZE_MAP[size];

  const showImage = src && !imgError && !loading;
  const initials = children?.slice(0, 2).toUpperCase();
  const label = ariaLabel || alt || (initials ? `Avatar ${initials}` : 'Avatar');

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        width: dimension,
        height: dimension,
      }}
    >
      <MuiAvatar
        ref={ref}
        id={id}
        src={showImage ? src : undefined}
        alt={showImage ? alt : undefined}
        aria-label={!showImage ? label : undefined}
        className={cn(className)}
        data-testid={testId}
        imgProps={{
          onError: () => setImgError(true),
        }}
        style={{
          width: dimension,
          height: dimension,
          fontSize: tokens.fontSizeCaption,
          fontWeight: tokens.fontWeightMedium,
          backgroundColor: tokens.brandPrimary,
          color: tokens.onPrimary,
          border: bordered
            ? `${tokens.borderWidthDefault}px solid ${tokens.borderDefault}`
            : undefined,
        }}
      >
        {!showImage && !loading && (initials || <PersonIcon style={{ fontSize: dimension * 0.55 }} />)}
      </MuiAvatar>
      {loading && (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tokens.surfaceOverlay,
            borderRadius: tokens.radiusFull,
          }}
        >
          <Spinner size="sm" decorative />
        </span>
      )}
    </span>
  );
});

Avatar.displayName = 'Avatar';
